import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef } from "react";
import { AlertIcon, CheckIcon, CrossIcon, InfoIcon } from "./Icons.js";
import { Button } from "./Button.js";
import { formatCountdown } from "../tokens.js";
import { useCountdown, useEscape } from "../hooks.js";
function toneIcon(tone, size = 16) {
    switch (tone) {
        case "error":
            return _jsx(CrossIcon, { size: size });
        case "warning":
            return _jsx(AlertIcon, { size: size });
        case "success":
            return _jsx(CheckIcon, { size: size });
        case "info":
            return _jsx(InfoIcon, { size: size });
    }
}
/** Field- or section-level. Icon + text, never colour alone (§15.4). */
export function InlineMessage({ tone, children, id }) {
    return (_jsxs("p", { id: id, className: `wr-inline-message wr-inline-message--${tone} wr-body-sm`, role: tone === "error" ? "alert" : "status", children: [_jsx("span", { "aria-hidden": "true", children: toneIcon(tone, 14) }), _jsx("span", { children: children })] }));
}
export function FormBanner({ tone, children, actions, takeFocus = true, testId }) {
    const ref = useRef(null);
    useEffect(() => {
        if (takeFocus)
            ref.current?.focus();
    }, [takeFocus]);
    return (_jsxs("div", { ref: ref, tabIndex: -1, className: `wr-form-banner wr-form-banner--${tone}`, role: tone === "error" || tone === "warning" ? "alert" : "status", "aria-live": tone === "error" || tone === "warning" ? "assertive" : "polite", "data-testid": testId, children: [_jsx("span", { className: "wr-form-banner__icon", "aria-hidden": "true", children: toneIcon(tone) }), _jsxs("div", { className: "wr-form-banner__body", children: [_jsx("span", { className: "wr-body-sm", children: children }), actions ? _jsx("div", { className: "wr-form-banner__actions", children: actions }) : null] })] }));
}
/**
 * A button that is disabled until a `m:ss` countdown reaches zero (§4).
 *
 * §7 LOCKED: on the verify screen the countdown starts **on arrival**, not on
 * first tap, because an email was already sent. That is expressed by the caller
 * mounting this with `seconds=60` — the component never decides when the clock
 * started.
 *
 * §15.7: the remaining time is not announced every second. `aria-live` is off
 * and the accessible name carries the state instead.
 */
export function CountdownAction({ seconds, label, waitingLabel, onAction, variant = "primary", size = "md", fullWidth = false, loading = false, loadingLabel, testId, }) {
    const { remaining } = useCountdown(seconds);
    const waiting = remaining > 0;
    const formatted = formatCountdown(remaining);
    return (_jsx(Button, { variant: variant, size: size, fullWidth: fullWidth, disabled: waiting, loading: loading, loadingLabel: loadingLabel, onClick: onAction, "data-testid": testId, children: waiting ? (_jsxs(_Fragment, { children: [_jsx("span", { children: waitingLabel ? waitingLabel(formatted) : label }), _jsx("span", { className: "wr-mono-md", children: formatted })] })) : (label) }));
}
/** A swipe, per the lobby spec §12.2: any direction, ≥40px. */
const TOAST_SWIPE_PX = 40;
/**
 * Bottom-anchored, 4s, one at a time (§4), with the **whole** §12.2 exit
 * inventory: tap, swipe in any direction ≥40px, the 4s timeout, a second toast
 * replacing it, `Esc`, and the route change that unmounts it.
 *
 * `Esc` and swipe were the two missing (T-191 N3). `Esc` matters because the
 * toast is the one piece of transient UI a keyboard user cannot tap away;
 * swipe, because on a phone the toast sits exactly where a thumb rests and the
 * gesture is the one people actually try. Neither makes it modal — it stays
 * `role="status"`, traps nothing and steals no focus, which is why the key
 * listener is on the document rather than on an element nothing has focused.
 *
 * ── The swipe was dead under a finger (T-193 Q1 / lobby §19.3) ──────────────
 *
 * Shipped, the row above was true for a mouse and false for a thumb. Quinn's
 * event trace: the element computed `touch-action: auto`, so at roughly 20 px
 * the browser claimed the gesture for scrolling and fired `pointercancel` /
 * `lostpointercapture`, tearing the handler down long before 40 px could be
 * measured. Ivy's ruling is the general one — **"every gesture row in an exit
 * inventory is a touch claim first and a pointer claim second"** — and it
 * names both acceptable fixes: declare a `touch-action` the browser cannot
 * override, **or** handle the touch stream directly.
 *
 * **Both ship here, and the redundancy is the point.**
 *
 *  1. `touch-action: none` on `.wr-toast` (components.css) stops the browser
 *     claiming the gesture at all, which is what makes the *pointer* path work
 *     under a finger in a real browser.
 *  2. Explicit `touchstart` / `touchmove` / `touchend` handlers measure the
 *     same 40 px off `Touch.clientX/Y`. This is not belt-and-braces for its own
 *     sake: a `touch-action` regression is invisible until somebody swipes on a
 *     phone, while the touch handlers are directly testable — a synthetic
 *     `TouchEvent` sequence in jsdom exercises them, and a `pointerdown` in
 *     jsdom cannot prove anything about a browser that never claims gestures
 *     there. **Verifying this row with a mouse does not verify it** (§19.3),
 *     and a test that could only be written with a mouse is the same defect.
 *
 * The two paths cannot double-fire: a browser that synthesises pointer events
 * from touches fires `pointerup` and `touchend` for one gesture, and dismissing
 * twice is harmless — the caller clears one piece of state.
 */
export function Toast({ message, onDismiss, duration = 4000 }) {
    const dismiss = useRef(onDismiss);
    dismiss.current = onDismiss;
    const swipeOrigin = useRef(null);
    /** The furthest the finger has travelled from its origin, in CSS px. */
    const touchTravel = useRef(0);
    useEffect(() => {
        const timer = setTimeout(() => dismiss.current(), duration);
        return () => clearTimeout(timer);
    }, [duration, message]);
    useEscape(true, onDismiss);
    return (_jsx("div", { className: "wr-toast-host", children: _jsx("div", { className: "wr-toast wr-body-sm", role: "status", "aria-live": "polite", "data-testid": "toast", onClick: onDismiss, onPointerDown: (event) => {
                swipeOrigin.current = { x: event.clientX, y: event.clientY };
                // Capture, so a swipe that travels off the toast still delivers its
                // `pointerup` here. Guarded because jsdom has no pointer capture.
                event.currentTarget.setPointerCapture?.(event.pointerId);
            }, onPointerUp: (event) => {
                const origin = swipeOrigin.current;
                swipeOrigin.current = null;
                if (!origin)
                    return;
                const travelled = Math.hypot(event.clientX - origin.x, event.clientY - origin.y);
                // A drag that ends on the toast fires `click` too; dismissing twice is
                // harmless (the caller clears one piece of state), and a swipe that
                // leaves the element never gets a `click` at all — which is the case
                // this branch exists for.
                if (travelled >= TOAST_SWIPE_PX)
                    onDismiss();
            }, 
            /**
             * The touch stream, handled directly (§19.3's second sanctioned fix).
             *
             * The **maximum** travel is kept rather than the final displacement: a
             * flick that overshoots and springs back, and a swipe whose last
             * `touchmove` lands nearer the origin than its middle did, are both
             * gestures a player made and expects to have worked. `touchend`'s own
             * `changedTouches` is folded in for the case where a browser delivers
             * no `touchmove` at all between start and end.
             */
            onTouchStart: (event) => {
                const touch = event.touches[0] ?? event.changedTouches[0];
                swipeOrigin.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
                touchTravel.current = 0;
            }, onTouchMove: (event) => {
                const origin = swipeOrigin.current;
                const touch = event.touches[0] ?? event.changedTouches[0];
                if (!origin || !touch)
                    return;
                touchTravel.current = Math.max(touchTravel.current, Math.hypot(touch.clientX - origin.x, touch.clientY - origin.y));
            }, onTouchEnd: (event) => {
                const origin = swipeOrigin.current;
                swipeOrigin.current = null;
                if (!origin)
                    return;
                const touch = event.changedTouches[0];
                const travelled = Math.max(touchTravel.current, touch ? Math.hypot(touch.clientX - origin.x, touch.clientY - origin.y) : 0);
                touchTravel.current = 0;
                if (travelled >= TOAST_SWIPE_PX)
                    onDismiss();
            }, children: message }) }));
}
/**
 * The brand loader (§5) — **LOCKED**.
 *
 * Five cells filling left to right on a 900ms loop. It is indeterminate and it
 * must never look like it represents real progress: no percentage, no total, no
 * easing that implies an end. Reduced motion leaves cells 1–3 statically
 * filled (handled in `base.css`).
 */
export function WordLoader({ label }) {
    return (_jsx("div", { className: "wr-loader", role: "status", "aria-label": label, "data-testid": "word-loader", children: [0, 1, 2, 3, 4].map((index) => (_jsx("span", { className: "wr-loader__cell", style: { animationDelay: `${index * 140}ms` } }, index))) }));
}
//# sourceMappingURL=Feedback.js.map